import { useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle2, Download, Loader2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './WithdrawalPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';
type Language = 'de' | 'en';
type PlanType = 'player' | 'team' | 'bundle' | 'unknown';

interface FormState {
    customerName: string;
    email: string;
    planType: PlanType;
    accountIdentifier: string;
    purchaseDate: string;
    stripeReference: string;
    website: string;
}

interface Receipt {
    reference: string;
    receivedAt: string;
    emailSent: boolean;
    confirmationText: string;
}

const initialForm: FormState = {
    customerName: '',
    email: '',
    planType: 'unknown',
    accountIdentifier: '',
    purchaseDate: '',
    stripeReference: '',
    website: '',
};

export function WithdrawalPage() {
    const [language, setLanguage] = useState<Language>('en');
    const [step, setStep] = useState<1 | 2>(1);
    const [form, setForm] = useState<FormState>(initialForm);
    const [declarationAccepted, setDeclarationAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [receipt, setReceipt] = useState<Receipt | null>(null);

    const copy = useMemo(() => language === 'de' ? {
        title: 'Vertrag widerrufen',
        intro: 'Hier können Sie den Widerruf eines StratSync-Premiumvertrags elektronisch erklären. Eine Begründung ist nicht erforderlich.',
        details: 'Vertrag identifizieren',
        name: 'Vor- und Nachname',
        email: 'E-Mail-Adresse aus der Stripe-Abrechnung',
        plan: 'Premium-Tarif',
        account: 'StratSync-Spielername oder Teamname',
        purchaseDate: 'Kaufdatum (optional)',
        stripeReference: 'Stripe-Rechnungs- oder Abonnementnummer (optional)',
        continue: 'Weiter',
        review: 'Angaben prüfen',
        declaration: 'Hiermit widerrufe ich den oben bezeichneten Vertrag.',
        confirm: 'Widerruf bestätigen',
        back: 'Zurück',
        received: 'Ihr Widerruf ist eingegangen',
        stored: 'Speichern Sie diese Referenz als Nachweis:',
        emailed: 'Eine Eingangsbestätigung wurde an die angegebene E-Mail-Adresse gesendet.',
        emailFailed: 'Die Anfrage wurde gespeichert. Die E-Mail konnte jedoch nicht zugestellt werden; laden Sie die Bestätigung deshalb jetzt herunter und kontaktieren Sie bei Bedarf contact@stratsync.pro.',
        download: 'Bestätigung herunterladen',
        home: 'Zurück zu StratSync',
        error: 'Der Widerruf konnte nicht übermittelt werden. Bitte prüfen Sie Ihre Angaben oder kontaktieren Sie contact@stratsync.pro.',
    } : {
        title: 'Withdraw from contract',
        intro: 'Use this form to electronically withdraw from an identified StratSync Premium contract. No reason is required.',
        details: 'Identify the contract',
        name: 'Full name',
        email: 'Email used for Stripe billing',
        plan: 'Premium plan',
        account: 'StratSync player name or team name',
        purchaseDate: 'Purchase date (optional)',
        stripeReference: 'Stripe invoice or subscription number (optional)',
        continue: 'Continue',
        review: 'Review your details',
        declaration: 'I hereby withdraw from the contract identified above.',
        confirm: 'Confirm withdrawal',
        back: 'Back',
        received: 'Your withdrawal was received',
        stored: 'Keep this reference as evidence:',
        emailed: 'A confirmation was sent to the email address provided.',
        emailFailed: 'The request was stored, but the email could not be delivered. Download the confirmation now and contact contact@stratsync.pro if needed.',
        download: 'Download confirmation',
        home: 'Back to StratSync',
        error: 'The withdrawal could not be submitted. Check your details or contact contact@stratsync.pro.',
    }, [language]);

    const update = (key: keyof FormState, value: string) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const proceed = () => {
        setError('');
        if (!form.customerName.trim() || !form.email.trim() || !form.accountIdentifier.trim()) {
            setError(language === 'de' ? 'Bitte füllen Sie alle Pflichtfelder aus.' : 'Complete all required fields.');
            return;
        }
        setStep(2);
    };

    const submit = async () => {
        if (!declarationAccepted || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const { data } = await axios.post<Receipt>(`${API}/stripe/withdrawals`, {
                ...form,
                declarationAccepted,
                language,
            });
            setReceipt(data);
        } catch {
            setError(copy.error);
        } finally {
            setSubmitting(false);
        }
    };

    const downloadReceipt = () => {
        if (!receipt) return;
        const blob = new Blob([receipt.confirmationText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `StratSync-Widerruf-${receipt.reference}.txt`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="withdrawal-page">
            <div className="withdrawal-shell">
                <header>
                    <Link to="/" aria-label="StratSync"><ArrowLeft size={18} /> StratSync</Link>
                    <button type="button" onClick={() => setLanguage(value => value === 'de' ? 'en' : 'de')}>
                        {language === 'de' ? 'English' : 'Deutsch'}
                    </button>
                </header>

                <section className="withdrawal-card">
                    {receipt ? (
                        <div className="withdrawal-success" role="status">
                            <CheckCircle2 size={48} />
                            <h1>{copy.received}</h1>
                            <p>{copy.stored}</p>
                            <strong>{receipt.reference}</strong>
                            <p className={receipt.emailSent ? '' : 'withdrawal-warning'}>
                                {receipt.emailSent ? copy.emailed : copy.emailFailed}
                            </p>
                            <button type="button" className="withdrawal-primary" onClick={downloadReceipt}>
                                <Download size={17} /> {copy.download}
                            </button>
                            <Link className="withdrawal-secondary-link" to="/">{copy.home}</Link>
                        </div>
                    ) : (
                        <>
                            <div className="withdrawal-heading">
                                <ShieldCheck size={34} />
                                <div><span>{step} / 2</span><h1>{copy.title}</h1></div>
                            </div>
                            <p className="withdrawal-intro">{copy.intro}</p>

                            {step === 1 ? (
                                <form onSubmit={event => { event.preventDefault(); proceed(); }}>
                                    <h2>{copy.details}</h2>
                                    <label>{copy.name}<input value={form.customerName} onChange={event => update('customerName', event.target.value)} maxLength={120} autoComplete="name" required /></label>
                                    <label>{copy.email}<input type="email" value={form.email} onChange={event => update('email', event.target.value)} maxLength={254} autoComplete="email" required /></label>
                                    <label>{copy.plan}<select value={form.planType} onChange={event => update('planType', event.target.value)}><option value="unknown">{language === 'de' ? 'Nicht sicher' : 'Not sure'}</option><option value="player">Player Premium</option><option value="team">Team Premium</option><option value="bundle">Team + 5 Player Premiums</option></select></label>
                                    <label>{copy.account}<input value={form.accountIdentifier} onChange={event => update('accountIdentifier', event.target.value)} maxLength={160} required /></label>
                                    <label>{copy.purchaseDate}<input type="date" value={form.purchaseDate} onChange={event => update('purchaseDate', event.target.value)} /></label>
                                    <label>{copy.stripeReference}<input value={form.stripeReference} onChange={event => update('stripeReference', event.target.value)} maxLength={160} /></label>
                                    <label className="withdrawal-honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={event => update('website', event.target.value)} /></label>
                                    {error && <div className="withdrawal-error" role="alert">{error}</div>}
                                    <button type="submit" className="withdrawal-primary">{copy.continue}</button>
                                </form>
                            ) : (
                                <div className="withdrawal-review">
                                    <h2>{copy.review}</h2>
                                    <dl><div><dt>{copy.name}</dt><dd>{form.customerName}</dd></div><div><dt>{copy.email}</dt><dd>{form.email}</dd></div><div><dt>{copy.plan}</dt><dd>{form.planType}</dd></div><div><dt>{copy.account}</dt><dd>{form.accountIdentifier}</dd></div>{form.purchaseDate && <div><dt>{copy.purchaseDate}</dt><dd>{form.purchaseDate}</dd></div>}{form.stripeReference && <div><dt>{copy.stripeReference}</dt><dd>{form.stripeReference}</dd></div>}</dl>
                                    <label className="withdrawal-declaration"><input type="checkbox" checked={declarationAccepted} onChange={event => setDeclarationAccepted(event.target.checked)} /><span>{copy.declaration}</span></label>
                                    {error && <div className="withdrawal-error" role="alert">{error}</div>}
                                    <div className="withdrawal-actions"><button type="button" className="withdrawal-back" onClick={() => setStep(1)} disabled={submitting}>{copy.back}</button><button type="button" className="withdrawal-primary" onClick={() => void submit()} disabled={!declarationAccepted || submitting}>{submitting ? <><Loader2 className="withdrawal-spin" size={17} /> {copy.confirm}</> : copy.confirm}</button></div>
                                </div>
                            )}
                        </>
                    )}
                </section>
                <footer><Link to="/imprint">Impressum</Link><Link to="/privacy">Datenschutz</Link><Link to="/terms">AGB</Link></footer>
            </div>
        </div>
    );
}
